import { useEffect, useState } from "react";
import { adminTerminalReaders, adminRegisterReader, adminRemoveReader } from "@/lib/api";

export const CardReadersModal = ({ onClose }) => {
    const [readers, setReaders] = useState(null);
    const [code, setCode] = useState("");
    const [label, setLabel] = useState("Kiosk 1");
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState({ text: "", err: false });

    const load = () => adminTerminalReaders()
        .then((r) => setReaders(r.readers))
        .catch((e) => { setReaders([]); setMsg({ text: e.response?.data?.detail || "Couldn't reach Stripe", err: true }); });
    useEffect(() => { load(); }, []);
    useEffect(() => { if (readers) setLabel(`Kiosk ${readers.length + 1}`); }, [readers?.length]); // eslint-disable-line react-hooks/exhaustive-deps

    const register = async () => {
        setBusy(true); setMsg({ text: "", err: false });
        try {
            const r = await adminRegisterReader(code, label);
            setCode("");
            setMsg({ text: `✓ ${r.label} registered — ${r.status === "online" ? "online and ready" : "registered, waiting to come online"}.`, err: false });
            await load();
        } catch (e) {
            setMsg({ text: e.response?.data?.detail || "Registration failed", err: true });
        } finally { setBusy(false); }
    };
    const remove = async (r) => {
        if (!window.confirm(`Remove ${r.label}? The kiosk using it will stop taking cards until you pick another reader.`)) return;
        try { await adminRemoveReader(r.id); await load(); } catch (e) { alert(e.response?.data?.detail || "Remove failed"); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="card-readers-modal">
            <div className="w-full max-w-lg bg-[var(--surface)] border border-[var(--border)] p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <p className="eyebrow">// Stripe card readers</p>
                        <h2 className="font-display text-[28px] leading-none mt-1">Card readers</h2>
                    </div>
                    <button onClick={onClose} className="btn-ghost" data-testid="card-readers-close">Done</button>
                </div>

                <div className="border border-[var(--gold)] p-4 mb-5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold mb-2">Add a reader</p>
                    <p className="text-[12px] text-muted mb-3">On the reader: swipe from the left edge → <b>Generate pairing code</b>. Type the 3 words below.</p>
                    <input className="input mb-2 lowercase" placeholder="e.g. apple-river-stone" value={code} onChange={(e) => setCode(e.target.value)} data-testid="reader-code-input" />
                    <input className="input mb-3" placeholder="Name (e.g. Kiosk 1)" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="reader-label-input" />
                    <button className="btn-primary w-full justify-center" disabled={busy || code.trim().length < 3 || !label.trim()} onClick={register} data-testid="reader-register-btn">
                        {busy ? "Registering…" : "Register reader"}
                    </button>
                    {msg.text && <p className={`font-mono text-[11px] mt-3 ${msg.err ? "text-[#ff5246]" : "text-gold"}`} data-testid="reader-msg">{msg.text}</p>}
                    <p className="text-muted text-xs mt-3">Then on the kiosk iPad open <a href="/kiosk/setup" className="text-gold underline" data-testid="reader-kiosk-setup-link">{window.location.host}/kiosk/setup</a> and enter the staff PIN to link it.</p>
                </div>

                <p className="eyebrow mb-2">// Registered</p>
                {readers === null ? <p className="text-muted text-sm">Loading…</p> : readers.length === 0 ? (
                    <p className="text-muted text-sm">No readers yet.</p>
                ) : (
                    <div className="divide-y divide-[var(--border)]">
                        {readers.map((r) => (
                            <div key={r.id} className="flex items-center gap-3 py-3" data-testid={`reader-row-${r.id}`}>
                                <span className={`w-2.5 h-2.5 rounded-full ${r.status === "online" ? "bg-[#1f9d55]" : "bg-[#ff5246]"}`} />
                                <div className="flex-1 min-w-0">
                                    <div className="text-[15px]">{r.label}</div>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted">{r.status} · {r.serial_number || r.id}</div>
                                </div>
                                <button className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted hover:text-[#ff5246]" onClick={() => remove(r)} data-testid={`reader-remove-${r.id}`}>Remove</button>
                            </div>
                        ))}
                    </div>
                )}
                <p className="text-[12px] text-muted mt-5">
                    Then on each kiosk iPad open <b>/kiosk/setup</b>, enter the staff PIN and pick its reader.
                </p>
            </div>
        </div>
    );
};
